/**
 * Event Context Utility
 *
 * Provides a lightweight, state-independent way to store and retrieve
 * contextual metadata for analytics events (e.g. source_name for the
 * currently viewed product). Any page can write to the context; useAuth
 * reads it when firing login/signup events.
 *
 * Storage key: 'zl_event_context' in localStorage.
 */

const EVENT_CONTEXT_KEY = 'zl_event_context';

export interface EventContext {
  source_name?: string;
  [key: string]: string | undefined;
}

/**
 * Merge new context values into the existing event context.
 * Safe to call on the server (no-op if window is undefined).
 */
export const setEventContext = (ctx: EventContext): void => {
  if (typeof window === 'undefined') return;
  try {
    const existing = getEventContext();
    localStorage.setItem(
      EVENT_CONTEXT_KEY,
      JSON.stringify({ ...existing, ...ctx }),
    );
  } catch {
    // Silently ignore storage errors (e.g. private browsing quota limits)
  }
};

/**
 * Read the current event context from localStorage.
 * Returns an empty object if nothing is stored or parsing fails.
 */
export const getEventContext = (): EventContext => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(EVENT_CONTEXT_KEY);
    return raw ? (JSON.parse(raw) as EventContext) : {};
  } catch {
    return {};
  }
};

/**
 * Clear the entire event context (e.g. after a logout).
 */
export const clearEventContext = (): void => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(EVENT_CONTEXT_KEY);
  } catch {
    // Silently ignore
  }
};
